import React, { type FocusEventHandler, useState } from "react";
import { twMerge } from "tailwind-merge";
import { type DPUserConfig, type DPTimeLimit, type DPDateExclude, DatePickerStateProvider } from "@rehookify/datepicker";

import { useControlledState } from "../../../hooks/useControlledState";
import { CommonColors } from "../../../types/colors";

import { DateInput } from "./components/DateInput";
import { CalendarPopover } from "./components/CalendarPopover";

export interface DatepickerProps {
  id?: string;
  name?: string;
  open?: boolean;
  defaultOpen?: boolean;
  value?: Date;
  defaultValue?: Date;
  color?: CommonColors;
  disabled?: boolean;
  placeholder?: string;
  // autoFocus?: boolean;
  className?: string;
  fluid?: boolean;
  format?: string;
  displayTimePicker?: boolean;
  minDate?: Date;
  maxDate?: Date;
  exclude?: DPDateExclude;
  minTime?: DPTimeLimit;
  maxTime?: DPTimeLimit;
  onOpen?: () => void;
  onClose?: () => void;
  onChange?: (value?: Date) => void;
  onFocus?: FocusEventHandler<HTMLButtonElement>;
  onBlur?: FocusEventHandler<HTMLButtonElement>;
}

export function SupDatepicker({
  id,
  name,
  open,
  defaultOpen,
  value,
  defaultValue,
  color,
  disabled,
  placeholder,
  className,
  fluid,
  displayTimePicker = false,
  format = displayTimePicker ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy",
  minDate,
  maxDate,
  exclude,
  onOpen,
  onClose,
  onChange,
  onFocus,
  onBlur,
}: DatepickerProps) {
  const [inputRef, setInputRef] = useState<HTMLButtonElement | null>(null);
  const [popoverVisible, setPopoverVisibility] = useControlledState({ value: open, defaultValue: defaultOpen, onStateChange: value => (value ? onOpen?.() : onClose?.()) });
  const [selectedDate, setSelectedDate] = useControlledState<Date | undefined>({ value, defaultValue, onStateChange: onChange });
  const [offsetDate, setOffsetDate] = useState(() => selectedDate ?? new Date());

  function closePopover() {
    setPopoverVisibility(false);
  }

  function handleClick() {
    setPopoverVisibility(!popoverVisible);
    setOffsetDate(selectedDate ?? new Date());
  }

  const handleFocus: FocusEventHandler<HTMLButtonElement> = e => {
    onFocus?.(e);
  };

  const handleBlur: FocusEventHandler<HTMLButtonElement> = e => {
    onBlur?.(e);
  };

  const handleChange = (date?: Date) => {
    if (date) setSelectedDate(date);
  };

  const handleClear = () => {
    setSelectedDate(undefined);
  };

  const config: DPUserConfig = {
    selectedDates: selectedDate ? [selectedDate] : [],
    offsetDate,
    onDatesChange: ([date]) => {
      setSelectedDate(date);
    },
    onOffsetChange: setOffsetDate,
    dates: {
      mode: "single",
      minDate,
      maxDate,
      toggle: true,
      selectSameDate: true,
    },
    calendar: {
      startDay: 1,
    },
    exclude,
    locale: {
      options: {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour12: true,
        hour: "2-digit",
        minute: "2-digit",
      },
    },
  };

  return (
    <DatePickerStateProvider config={config}>
      <div className={twMerge("sup-datepicker inline-flex", fluid && "w-full", className)}>
        <DateInput
          ref={setInputRef}
          id={id}
          name={name}
          fluid={fluid}
          placeholder={placeholder}
          color={color}
          disabled={disabled}
          format={format}
          value={selectedDate}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleClick}
          onClear={handleClear}
        />

        <CalendarPopover displayTimePicker={displayTimePicker} className="shadow" anchorEl={inputRef} open={popoverVisible} onClose={closePopover} onChange={handleChange} />
      </div>
    </DatePickerStateProvider>
  );
}
